/**
 * @file rgb-domain.ts
 * @author Rapoo Device SDK Team
 * @date 2026-07-21
 * @description Telink 键盘 RGB 灯效领域描述
 *
 * RGB 领域包含两种灯效：
 * - index 0: 充电灯（+0x0088 ~ +0x008F，8 字节）
 * - index 1: 氛围灯（+0x0090 ~ +0x0097，8 字节）
 *
 * 数据结构与动态灯效一致：brightness/speed/isColorful/rgb。
 * 此领域为 Telink 独有，CH585M 无此领域。
 */

import {
  LightingDomainDescriptor,
  LightingDomainType,
  LightingFeatureFlag,
} from '../../../../../types/lightingDomain'
import { getAddress } from '../../../../../utils/common'
import { ADDRESS_TELINK } from '../../../protocols/address'
import { sanitizeLightingLevel } from '../../../../shared/feature-impls/keyboard-lighting/lighting'

export const rgbDomain: LightingDomainDescriptor = {
  type: LightingDomainType.Indicator,
  configSize: 8,
  supportedFeatures:
    LightingFeatureFlag.Brightness |
    LightingFeatureFlag.Speed |
    LightingFeatureFlag.SingleColor |
    LightingFeatureFlag.Colorful,

  addressResolver: {
    getBaseAddr: (configIndex, itemIndex) => {
      // index 0 = 充电灯, index 1 = 氛围灯
      const addr =
        itemIndex === 0 ? ADDRESS_TELINK.CHARGE_LIGHT_PARAM : ADDRESS_TELINK.AMBIENT_LIGHT_PARAM
      return getAddress(addr, configIndex)
    },
    getFieldAddr: (field, configIndex, itemIndex) => {
      const fieldOffsetMap: Record<string, number> = {
        brightness: 0,
        speed: 1,
        isColorful: 2,
      }
      const offset = fieldOffsetMap[field] ?? 0
      const baseAddr =
        itemIndex === 0 ? ADDRESS_TELINK.CHARGE_LIGHT_PARAM : ADDRESS_TELINK.AMBIENT_LIGHT_PARAM
      const addr = getAddress(baseAddr, configIndex)
      // getAddress 返回的是 ADDRESS_TELINK 地址表中的活引用，就地叠加偏移会永久改写常量表。
      // 必须拷贝后再叠加，并让低字节进位到高字节。
      const shifted = [...addr]
      let carry = offset
      for (let i = 0; i < shifted.length && carry !== 0; i++) {
        const sum = shifted[i] + carry
        shifted[i] = sum & 0xff
        carry = sum >> 8
      }
      return shifted
    },
  },

  parser: {
    parse: (buffer, index) => ({
      index,
      domainType: LightingDomainType.Indicator,
      brightness: sanitizeLightingLevel(buffer[0]),
      speed: sanitizeLightingLevel(buffer[1]),
      isColorful: buffer[2] === 0,
      rgb: { r: buffer[4], g: buffer[5], b: buffer[6] },
      buffer,
    }),
    serialize: (config) => {
      const buf = new Uint8Array(8)
      buf[0] = sanitizeLightingLevel(config.brightness)
      buf[1] = sanitizeLightingLevel(config.speed)
      buf[2] = config.isColorful ? 0 : 1
      buf[3] = 0
      buf[4] = config.rgb?.r ?? 255
      buf[5] = config.rgb?.g ?? 255
      buf[6] = config.rgb?.b ?? 255
      buf[7] = 0
      return buf
    },
  },

  cacheTags: (index, configIndex) => [`light:indicator:${index}`, `cfg:${configIndex}`],
}
